import { useEffect, useRef } from 'react';
import moment from 'moment';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { updatedCurrentSelectedPatientIndex } from 'redux/familyBookingSlice';
import { capitalizeAvatarName, concatFullName } from 'utils/common';
import { checkIsSamePatient } from 'utils/checkIsSamePatient';

import styles from './PatientInfoSelection.module.scss';

interface PatientInfoSelectionProps {
  isLocked: boolean;
}

const PatientInfoSelection = ({ isLocked }: PatientInfoSelectionProps) => {
  const {
    familyBookingSlice: {
      selectedPatients,
      currentSelectedPatientIndex,
      contactUser,
    },
    clinicDetailsSlice: {
      clinicData: { services },
    },
  } = useAppSelector((state) => state);
  const dispatch = useAppDispatch();

  const activeCardRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeCardRef.current?.scrollIntoView?.({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [currentSelectedPatientIndex]);

  const firstUnscheduledPatientIndex = selectedPatients.findIndex(
    (patient) => !patient.isSlotSelected
  );

  const hasUnscheduledPatient = firstUnscheduledPatientIndex !== -1;

  const isContactUser = (index: number) =>
    checkIsSamePatient(selectedPatients[index], contactUser);

  return (
    <div className={styles['container']}>
      {selectedPatients.map((patient, index) => {
        const isActive = currentSelectedPatientIndex === index;

        const isAwaitingTurn =
          hasUnscheduledPatient && index > firstUnscheduledPatientIndex;

        const serviceName = services.find(
          (service) => service.id === patient.serviceId
        )?.name;
        const patientName = concatFullName(patient.firstName, patient.lastName);
        const hasSlot =
          patient.isSlotSelected &&
          !!patient.appointmentDate &&
          !!patient.startTime;

        return (
          <button
            type="button"
            className={classNames(styles['card'], {
              [styles['card-active']]: isActive,
              [styles['card-complete']]: hasSlot,
              [styles['card-awaiting-turn']]: isAwaitingTurn,
            })}
            key={`${patient.id || patient.email || patientName}-${index}`}
            ref={isActive ? activeCardRef : undefined}
            disabled={isLocked || isAwaitingTurn}
            onClick={() => dispatch(updatedCurrentSelectedPatientIndex(index))}
          >
            <span className={styles['card-header']}>
              <span className={styles['initials']}>
                {capitalizeAvatarName(patientName)}
              </span>

              <span className={styles['card-main']}>
                <span className={styles['name']}>
                  <span className={styles['patient-name']}>{patientName}</span>
                  {isContactUser(index) && (
                    <small>(You)</small>
                  )}
                </span>

                <span className={styles['card-body']}>
                  {serviceName && (
                    <span className={styles['service']}>{serviceName}</span>
                  )}

                  {hasSlot ? (
                    <span className={styles['slot-datetime']}>
                      {`${moment(patient.appointmentDate).format(
                        'MMM D, YYYY'
                      )} · ${moment(patient.startTime, 'HH:mm:ss').format(
                        'h:mm A'
                      )}`}
                    </span>
                  ) : (
                    <span className={styles['slot-empty']}>
                      {isActive ? 'Choose a time' : '-'}
                    </span>
                  )}
                </span>
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default PatientInfoSelection;
